import { Component, EventEmitter, Output, HostListener, ElementRef, OnInit } from '@angular/core';
import { FilterSectionsToUpdate, ImportFilterConfirm } from '../models/filter-import.models';
import { FilterService } from '../services/filter.service';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { PoeApiService, PoeFilter } from '../services/poe-api.service';
import { OAuthService } from '../services/o-auth.service';
import { LOCAL_STORAGE_KEY_ACCOUNT_FILTERS, LOCAL_STORAGE_KEY_FILTER_DEFAULT_REALM, LOCAL_STORAGE_KEY_LAST_LOADED_FILTERS, MAX_FILTER_NAME_LENGTH, VERSION } from '../const';
import { Filter, StoredFilter } from '../models/filter-models';
import { totalRulesInCustomGroups } from '../common/custom-rules/custom-rule-groups-helper';
import { FilterImporterService } from '../services/filter-importer.service';
import { patchVersion } from '../../version';
import { FilterGeneratorService } from '../services/filter-generator.service';
import { logEvent } from '../app.config';
import { ImportFilterDialogComponent } from '../import-filter-dialog/import-filter-dialog.component';
import { TooltipWrapperComponent } from '../common/tooltip-wrapper/tooltip-wrapper.component';
import { ACCOUNT_FILTER_DEFAULT_REALM, DEFAULT_FILTER_NAME, FILTER_DESCRIPTION, SISTER_FILTER_DESCRIPTION, sisterDisplayDomainName } from '../../game';
import { BaseComponent } from '../common/base.component';
import { FilterSnapshotService } from '../services/filter-snapshot.service';
import { ToastService } from '../services/toast.service';
import { ItemDataService } from '../services/item-data.service';
import {
  ACTIVE_FILTER_ICON_ALLOWLIST,
  resolveActiveFilterIcons,
} from '../services/active-filter-icons';
import { SelectComponent, SelectOptionComponent } from '../common/select/select.component';

const AUTO_ICON_VALUE = '';

@Component({
  selector: 'app-filter-selector-dialog',
  templateUrl: './filter-selector-dialog.component.html',
  styleUrls: ['../common/dialogs.scss', './filter-selector-dialog.component.scss'],
  imports: [FormsModule, DatePipe, ImportFilterDialogComponent, TooltipWrapperComponent, SelectComponent, SelectOptionComponent]
})
export class FilterSelectorDialogComponent extends BaseComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @Output() updateFilter = new EventEmitter<ImportFilterConfirm>();

  oldName = '';
  renameError: string | null = null;
  editingFilterKey: string | null = null;
  editingIconKey: string | null = null;
  editingIconValue: string = AUTO_ICON_VALUE;
  readonly iconAllowlist = ACTIVE_FILTER_ICON_ALLOWLIST;
  readonly autoIconValue = AUTO_ICON_VALUE;
  /** Precomputed display icons per filterKey — refreshed on open and after icon edits. */
  filterDisplayIcons: Record<string, string[]> = {};
  readonly maxFilterNameLength = MAX_FILTER_NAME_LENGTH;
  loggedIn = false;
  showForceRefresh = false;

  retrievingFilters = false;
  filtersRetrieved = false;
  errorRetrievingFilters: string | null = null;

  downloadingFilter: PoeFilter | null = null;
  filterDownloaded = false;
  errorDownloadingFilters: string | null = null;

  uploadingFilter: StoredFilter | null = null;
  filterUploaded = false;
  errorUploadingFilter: string | null = null;

  allAccountFilters: PoeFilter[] = [];
  toolAccountFilters: PoeFilter[] = [];
  sisterToolFilters: string | null = null;
  otherFilters: string | null = null;
  readonly sisterDisplayDomainName = sisterDisplayDomainName;

  copyingSections = false;
  refreshingFromOnline = false;
  showImportDialog = false;
  fromFilter: StoredFilter | null = null;
  toFilter: StoredFilter | null = null;
  importDialogSectionsToUpdate = new FilterSectionsToUpdate();

  constructor(private elementRef: ElementRef, public filterService: FilterService,
    private loginService: OAuthService, private poeApiService: PoeApiService,
    private filterGeneratorService: FilterGeneratorService,
    private filterImporterService: FilterImporterService,
    private filterSnapshotService: FilterSnapshotService,
    private toastService: ToastService,
    private itemDataService: ItemDataService) {
    super();
  }

  customRuleTotal(f: Filter): number {
    return totalRulesInCustomGroups(f.customRuleGroups);
  }

  cosmeticRuleTotal(f: Filter): number {
    return totalRulesInCustomGroups(f.customCosmeticRuleGroups);
  }

  ngOnInit(): void {
    this.refreshFilterDisplayIcons();

    const lastLoadedFiltersString = localStorage.getItem(LOCAL_STORAGE_KEY_LAST_LOADED_FILTERS);
    const lastLoadedFiltersDate = lastLoadedFiltersString ? new Date(Number(lastLoadedFiltersString)) : null;

    if (!lastLoadedFiltersDate || new Date().getTime() - lastLoadedFiltersDate.getTime() > 1000 * 60 * 10) {
      if (this.loginService.userName()) {
        this.loggedIn = true;
        this.retrieveFilters();
      }
    } else {
      this.loggedIn = true;
      this.showForceRefresh = true;
      const storedAccountFilters = localStorage.getItem(LOCAL_STORAGE_KEY_ACCOUNT_FILTERS);
      if (storedAccountFilters) {
        this.allAccountFilters = JSON.parse(storedAccountFilters);
        this.buildAccountAndOtherFilters();
        this.filtersRetrieved = true;
      }
    }
  }

  refreshFilterDisplayIcons(): void {
    const weapons = this.itemDataService.getAllWeaponTypes();
    const next: Record<string, string[]> = {};
    for (const filter of this.filterService.storedFilters) {
      next[this.filterKey(filter)] = resolveActiveFilterIcons(filter, weapons).icons;
    }
    this.filterDisplayIcons = next;
  }

  beginEditIcon(event: Event, filter: StoredFilter): void {
    event.preventDefault();
    event.stopPropagation();
    this.editingFilterKey = null;
    this.editingIconKey = this.filterKey(filter);
    this.editingIconValue = filter.icon ?? AUTO_ICON_VALUE;
  }

  onIconSelected(filter: StoredFilter, value: string): void {
    if (value === AUTO_ICON_VALUE) {
      delete filter.icon;
    } else {
      filter.icon = value;
    }
    this.editingIconKey = null;
    this.filterService.saveFilters();
    this.filterService.dirtyFilterUpdated.update(c => c + 1);
    this.refreshFilterDisplayIcons();
  }

  retrieveFilters(event?: MouseEvent) {
    if (event) event.preventDefault();
    this.retrievingFilters = true;

    this.poeApiService.getFilters()
      .then(poeFilters => {
        this.allAccountFilters = poeFilters;
        localStorage.setItem(LOCAL_STORAGE_KEY_ACCOUNT_FILTERS , JSON.stringify(poeFilters));

        this.buildAccountAndOtherFilters();

        this.filtersRetrieved = true;
        this.showForceRefresh = false;
        localStorage.setItem(LOCAL_STORAGE_KEY_LAST_LOADED_FILTERS, Date.now().toString());
      })
      .catch(error => {
        this.errorRetrievingFilters = error;
      })
      .finally(() => {
        this.retrievingFilters = false;
      });
  }

  private buildAccountAndOtherFilters() {
    const notStoredLocally = (f: PoeFilter) =>
      !this.filterService.storedFilters.find(sf => sf.id === f.id);

    this.toolAccountFilters = this.allAccountFilters.filter(f =>
      f.description === FILTER_DESCRIPTION &&
      notStoredLocally(f) &&
      f.version
    );

    this.sisterToolFilters = this.allAccountFilters.filter(f =>
      f.description === SISTER_FILTER_DESCRIPTION &&
      notStoredLocally(f)
    ).map(f => f.filter_name).join(', ') || null;

    this.otherFilters = this.allAccountFilters.filter(f =>
      notStoredLocally(f) &&
      f.description !== SISTER_FILTER_DESCRIPTION &&
      (f.description !== FILTER_DESCRIPTION || !f.version)
    ).map(f => f.filter_name).join(', ') || null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const dialogElement = this.elementRef.nativeElement.querySelector('.dialog');
    if (dialogElement && !dialogElement.contains(target)) {
      this.closeDialog();
      return;
    }

    if (this.editingIconKey && !target.closest('.filter-icon-edit')) {
      this.editingIconKey = null;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent) {
    if (!this.editingIconKey) return;
    event.preventDefault();
    event.stopPropagation();
    this.editingIconKey = null;
  }

  selectFilter(filterName: string) {
    this.editingIconKey = null;
    this.filterService.setActiveFilter(filterName);
    this.updateFilter.emit();
  }

  closeDialog() {
    this.close.emit();
  }

  filterKey(filter: StoredFilter): string {
    return filter.filter.id || filter.name;
  }

  beginRename(event: Event, filter: StoredFilter) {
    event.stopPropagation();
    this.renameError = null;
    this.oldName = filter.name;
    this.editingIconKey = null;
    this.editingFilterKey = this.filterKey(filter);
    setTimeout(() => {
      const input = this.elementRef.nativeElement.querySelector('.filter-name-input') as HTMLInputElement | null;
      input?.focus();
      input?.select();
    });
  }

  nameChangeKeyDown(event: KeyboardEvent, filter?: StoredFilter) {
    if (event.key === 'Enter') {
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
      return;
    }
    if (event.key === 'Escape' && filter) {
      event.preventDefault();
      filter.name = this.oldName;
      this.editingFilterKey = null;
      return;
    }

    const allowedChars = /^[a-zA-Z0-9 .-]$/;
    const char = event.key;

    if (event.ctrlKey || event.altKey || event.metaKey ||
      char.length > 1 || char === 'Backspace' || char === 'Delete' ||
      char === 'ArrowLeft' || char === 'ArrowRight' ||
      char === 'Tab' || char === 'Home' || char === 'End') {
      return;
    }

    if (!allowedChars.test(char)) {
      event.preventDefault();
    }
  }

  finishRename(event: Event, filter: StoredFilter) {
    if (this.editingFilterKey !== this.filterKey(filter)) {
      return;
    }
    this.editingFilterKey = null;
    this.renameError = null;
    const input = event.target as HTMLInputElement;
    let newName = input.value.trim();
    if (newName.length > MAX_FILTER_NAME_LENGTH) {
      newName = newName.substring(0, MAX_FILTER_NAME_LENGTH);
    }
    if (!newName) {
      filter.name = this.oldName;
      return;
    }
    const existingNames = this.filterService.storedFilters.map(f => f.name).concat(this.allAccountFilters.map(f => f.filter_name));
    if (existingNames.filter(n => n === newName).length > 1) {
      filter.name = this.oldName;
      this.renameError = `"${newName}" already exists. Please choose a different name.`;
      return;
    }
    filter.name = newName;
    this.filterService.updateFilterName(this.oldName, newName);
    this.filterService.saveFilters();
    logEvent('filter', 'name_changed');
  }

  login($event: any) {
    $event.preventDefault();
    this.loginService.login();
  }

  copyShareLink($event: Event, filter: StoredFilter) {
    $event.preventDefault();
    $event.stopPropagation();

    const url = this.filterSnapshotService.buildShareUrl(filter);
    if (!url) return;

    void navigator.clipboard.writeText(url).then(() => {
      this.toastService.raise('success', 'Share link copied to clipboard.');
    });
  }

  upload(event: MouseEvent, storedFilterToUpload: StoredFilter) {
    event.preventDefault();
    event.stopPropagation();

    const realm = localStorage.getItem(LOCAL_STORAGE_KEY_FILTER_DEFAULT_REALM) || ACCOUNT_FILTER_DEFAULT_REALM;
    this.uploadingFilter = storedFilterToUpload;
    const currentFilter = this.filterService.activeFilter();
    this.filterService.setActiveFilter(storedFilterToUpload.name);
    const generatedFilter = this.filterGeneratorService.updateFilter(storedFilterToUpload, true);
    this.filterService.setActiveFilter(currentFilter.name);
    if (storedFilterToUpload.id) {
      this.poeApiService.updateFilter(storedFilterToUpload.id, storedFilterToUpload.name, generatedFilter.filterTextFull, realm)
        .then(() => {
          logEvent('filter', 'upload_update');
        })
        .catch(error => {
          this.errorUploadingFilter = (error?.toString() || 'Unknown error').substring(0, 100);
          logEvent('filter', 'upload_update_error', this.errorUploadingFilter ?? undefined);
        })
        .finally(() => {
          this.uploadingFilter = null;
        });
    } else {
      this.poeApiService.createFilter(storedFilterToUpload.name, generatedFilter.filterTextFull, realm)
        .then(createdFilter => {
          storedFilterToUpload.id = createdFilter.id;
          this.filterService.setFilterId(storedFilterToUpload.name, storedFilterToUpload.id);
          this.allAccountFilters.push({
            id: storedFilterToUpload.id,
            filter_name: storedFilterToUpload.name,
            realm,
            description: FILTER_DESCRIPTION,
            version: storedFilterToUpload.version,
            type: 'Normal',
            public: true,
          });
          localStorage.setItem(LOCAL_STORAGE_KEY_ACCOUNT_FILTERS, JSON.stringify(this.allAccountFilters));
          this.buildAccountAndOtherFilters();
          logEvent('filter', 'upload_initial', realm);
        })
        .catch(error => {
          this.errorUploadingFilter = (error?.toString() || 'Unknown error').substring(0, 100);
          logEvent('filter', 'upload_initial_error', this.errorUploadingFilter ?? undefined);
        })
        .finally(() => {
          this.uploadingFilter = null;
        });
    }
  }

  download(accountFilter: PoeFilter) {
    this.downloadingFilter = accountFilter;
    this.filterDownloaded = false;
    this.poeApiService.getFilter(accountFilter.id)
      .then(details => {
        try {
          const importResult = this.filterImporterService.importFilter(details.filter!);
          if (importResult.importFilterError) {
            this.errorDownloadingFilters = importResult.importFilterError;
            return;
          } else {
            const storedFilter = {
              name: details.filter_name,
              filter: importResult.filter,
              version: VERSION,
              build: patchVersion,
              lastUpdated: new Date().toISOString(),
              id: details.id,
            } as StoredFilter;
            this.filterService.addFilter(storedFilter);
            this.filterDownloaded = true;
            this.toolAccountFilters = this.toolAccountFilters.filter(f => f.id !== accountFilter.id);
            logEvent('filter', 'download');
          }
        } catch (error) {
          this.errorDownloadingFilters = (error?.toString() || 'Unknown error').substring(0, 100);
          logEvent('filter', 'download_error', this.errorDownloadingFilters ?? undefined);
        }
      })
      .catch(error => {
        this.errorDownloadingFilters = (error?.toString() || 'Unknown error').substring(0, 100);
        logEvent('filter', 'download_error', this.errorDownloadingFilters ?? undefined);
      })
      .finally(() => {
        this.downloadingFilter = null;
      });
  }

  duplicate(event: MouseEvent, filter: StoredFilter) {
    event.preventDefault();
    event.stopPropagation();

    let counter = 0;
    let newName: string;
    do {
      const suffix = counter === 0 ? ' - copy' : ` - copy ${counter}`;
      newName = `${filter.name.substring(0, MAX_FILTER_NAME_LENGTH - suffix.length)}${suffix}`;
      counter++;
    } while (this.filterService.storedFilters.some(f => f.name === newName));

    const newFilter = new StoredFilter(newName, this.filterService.createNewFilter(), filter.version, filter.build, filter.lastUpdated);
    if (filter.icon) {
      newFilter.icon = filter.icon;
    }
    this.toFilter = newFilter;
    this.fromFilter = filter;
    this.importDialogSectionsToUpdate = FilterSectionsToUpdate.allRulesOn();
    this.showImportDialog = true;
  }

  copySections(event: MouseEvent, filter: StoredFilter) {
    event.preventDefault();
    event.stopPropagation();

    this.copyingSections = true;
    this.fromFilter = filter;
  }

  pasteSections(event: MouseEvent, filter: StoredFilter) {
    event.preventDefault();
    event.stopPropagation();

    this.toFilter = filter;
    this.importDialogSectionsToUpdate = FilterSectionsToUpdate.fileImportDefaults();
    this.showImportDialog = true;
  }

  async refreshFromOnline(event: Event, filter: StoredFilter) {
    event.preventDefault();
    event.stopPropagation();

    const filterId = filter.importedFromFilterId;
    if (!filterId) return;

    const importResult = await this.filterSnapshotService.loadSharedFilter({
      filterId,
      username: filter.importedFromUsername,
    });
    if (!importResult?.filter) return;

    this.fromFilter = {
      name: importResult.importedName || filter.name,
      filter: importResult.filter,
      version: filter.version,
      build: filter.build,
      lastUpdated: filter.lastUpdated,
    };
    this.toFilter = filter;
    this.refreshingFromOnline = true;
    this.importDialogSectionsToUpdate = FilterSectionsToUpdate.allRulesOn();
    this.showImportDialog = true;
  }

  closeImportDialog() {
    this.showImportDialog = false;
    this.refreshingFromOnline = false;
  }

  updateFilterSections(confirm: ImportFilterConfirm) {
    this.filterService.updateFilterSections(this.fromFilter!.filter, confirm.sections, this.toFilter!.filter);

    if (this.refreshingFromOnline) {
      if (this.fromFilter?.name) {
        this.toFilter!.importedFromFilterName = this.fromFilter.name;
      }
      this.filterService.saveFilters();
      logEvent('filter', 'refresh_from_online');
    } else if (!this.copyingSections) {
      this.filterService.addFilter(this.toFilter!);
      this.refreshFilterDisplayIcons();
      logEvent('filter', 'duplicate');
    } else {
      logEvent('filter', 'copy_sections');
    }

    this.toFilter = null;
    this.fromFilter = null;
    this.showImportDialog = false;
    this.copyingSections = false;
    this.refreshingFromOnline = false;
  }

  remove(event: MouseEvent, filter: StoredFilter) {
    event.preventDefault();
    event.stopPropagation();

    this.filterService.removeFilter(filter.name);

    const remaining = this.filterService.storedFilters;
    if (remaining.length > 0) {
      this.filterService.setActiveFilter(remaining[0].name);
    } else {
      const storedFilter: StoredFilter = {
        name: DEFAULT_FILTER_NAME,
        filter: this.filterService.createNewFilter(),
        lastUpdated: new Date().toISOString(),
        version: VERSION,
        build: patchVersion
      };
      this.filterService.addFilter(storedFilter);
      this.filterService.setActiveFilter(storedFilter.name);
    }

    this.refreshFilterDisplayIcons();
    this.buildAccountAndOtherFilters();
    logEvent('filter', 'remove');
  }

  createNew() {
    let uniqueName = 'New Filter';
    let counter = 1;
    while (this.filterService.storedFilters.some(f => f.name === uniqueName)) {
      uniqueName = `New Filter ${counter++}`;
    }

    const newFilter = {
      name: uniqueName,
      filter: this.filterService.createNewFilter(),
      version: VERSION,
      build: patchVersion,
      lastUpdated: new Date().toISOString(),
    } as StoredFilter;

    this.filterService.addFilter(newFilter);
    this.refreshFilterDisplayIcons();
    this.selectFilter(uniqueName);
    logEvent('filter', 'new');
  }
}
